/**
 * DateTimeViewer Component (Ported from GEOADMIN geoadmin_react)
 * Displays date/time with interactive toggle between relative time ("3 ngày trước")
 * and formatted date ("12/08/2026").
 * Mặc định ở mode relative theo yêu cầu.
 */
import React, { useState, useMemo } from 'react';
import { Text, TouchableOpacity, StyleSheet } from 'react-native';
import { colors, fontWeights } from '../theme';

export interface DateTimeViewerProps {
  date?: string | number | Date | null;
  initialMode?: 'relative' | 'formatted';
  showSeconds?: boolean;
  showTooltip?: boolean;
  style?: any;
}

/**
 * Format a date to standard DD/MM/YYYY
 */

function parseDateInput(input?: string | number | Date | null): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === 'number') return new Date(input);

  const str = String(input).trim();
  if (!str) return null;

  // Handle DD/MM/YYYY or DD/MM/YYYY HH:mm:ss format
  if (str.includes('/')) {
    const spaceParts = str.split(' ');
    const datePart = spaceParts[0];
    const timePart = spaceParts[1];
    const [d, m, y] = datePart.split('/').map((s) => parseInt(s, 10));
    if (d && m && y) {
      if (!timePart) {
        const now = new Date();
        // If it's today's date without time specified, return current time so relative time displays "Vừa xong"
        if (d === now.getDate() && m === (now.getMonth() + 1) && y === now.getFullYear()) {
          return now;
        }
      }
      const [hh, mm, ss] = (timePart || '00:00:00').split(':').map((s) => parseInt(s, 10) || 0);
      return new Date(y, m - 1, d, hh, mm, ss);
    }
  }

  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(dateInput?: string | number | Date | null): string {
  const d = parseDateInput(dateInput);
  if (!d) return typeof dateInput === 'string' ? dateInput : '-';

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

export function formatRelativeTime(dateInput?: string | number | Date | null): string {
  const d = parseDateInput(dateInput);
  if (!d) return typeof dateInput === 'string' ? dateInput : '-';

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();

  if (diffMs < 0) {
    return 'Vừa xong';
  }

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'Vừa xong';

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} phút trước`;

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} giờ trước`;

  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 30) return `${diffDay} ngày trước`;

  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth} tháng trước`;

  const diffYear = Math.floor(diffDay / 365);
  return `${diffYear} năm trước`;
}

export const DateTimeViewer: React.FC<DateTimeViewerProps> = ({
  date,
  initialMode = 'relative',
  style,
}) => {
  const [mode, setMode] = useState<'relative' | 'formatted'>(initialMode);

  const formattedStr = useMemo(() => formatDateTime(date), [date]);
  const relativeStr = useMemo(() => formatRelativeTime(date), [date]);

  if (!date) {
    return <Text style={[styles.text, style]}>-</Text>;
  }

  const toggleMode = () => {
    setMode((prev) => (prev === 'relative' ? 'formatted' : 'relative'));
  };

  const displayText = mode === 'relative' ? relativeStr : formattedStr;

  return (
    <TouchableOpacity onPress={toggleMode} activeOpacity={0.7} accessibilityLabel={`Thời gian: ${displayText}`}>
      <Text style={[styles.text, style]}>
        {displayText}
      </Text>
    </TouchableOpacity>
  );
};

export default DateTimeViewer;

const styles = StyleSheet.create({
  text: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },
});
